import { Typography } from '@breetometer/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { OtpForm } from 'BreetComponents';
import { alwaysRefetchApiQueryOptions, handleApiError, type IApiResponse, OTPLENGTH, OTPVerificationSchema } from 'BreetHelpers';
import { useGenerateOTPQuery, useVerifyOTPMutation } from 'BreetRedux';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import type { IAccessCredentialComponentProps } from '../DialogAccessCredentials.helpers';

const Otp = ({
	description,
	title,
	type,
	dispatchAccessCredentials,
	inputLength = OTPLENGTH,
	generateOTP = true,
	refetchFn,
	resendOtp,
	buttonTitle,
	otpCta,
	error,
	clearError,
}: IAccessCredentialComponentProps) => {
	const {
		refetch,
		isLoading: resendOtpLoading,
		isFetching: resendOtpFetching,
		isUninitialized,
	} = useGenerateOTPQuery(undefined, {
		...alwaysRefetchApiQueryOptions,
		skip: !generateOTP,
	});

	const [verifyOTP, { isLoading }] = useVerifyOTPMutation();

	const {
		setValue,
		handleSubmit,
		setError,
		clearErrors,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(OTPVerificationSchema),
	});

	const handleOnVerification = useCallback(
		(otpArg: string) => {
			if (type === 'collect') {
				dispatchAccessCredentials(otpArg);
			} else {
				verifyOTP({ otp: otpArg })
					.unwrap()
					.then((data) => {
						if (!data.data) return;

						dispatchAccessCredentials(otpArg);
					})
					.catch((error: unknown) => {
						const errmrData = error as { data: IApiResponse } | undefined;
						setError('otp', { message: errmrData?.data.message });
					});
			}
		},
		[type, dispatchAccessCredentials, verifyOTP, setError]
	);

	const onChange = useCallback(
		(otp: string) => {
			setValue('otp', otp);

			if (!otp) {
				clearErrors('otp');
				clearError?.();
				return;
			}

			if (otp.length === inputLength) {
				handleOnVerification(otp);
				clearErrors('otp');
			}
		},
		[setValue, inputLength, handleOnVerification, clearErrors, clearError]
	);

	const onResend = useCallback(
		(restartCountDown?: () => void) => {
			const dataRefetch = () => {
				if (refetchFn) {
					return refetchFn();
				}

				if (!isUninitialized) {
					return refetch().unwrap();
				}

				return Promise.resolve();
			};

			dataRefetch()
				.then(() => {
					restartCountDown?.();
					clearErrors('otp');
					clearError?.();
				})
				.catch(handleApiError);
		},
		[refetchFn, refetch, clearErrors, clearError, isUninitialized]
	);

	const onSubmit = useCallback(
		(payload: { otp: string }) => {
			handleOnVerification(payload.otp);
			clearErrors('otp');
		},
		[handleOnVerification, clearErrors]
	);

	const otpValue = watch('otp');

	return (
		<div className='otpverification bg-primary-500'>
			<Typography
				as='h5'
				className='color-neutral-50'
			>
				{title ?? 'Verify OTP'}
			</Typography>

			{description && (
				<Typography
					className='color-primary-100'
					dangerouslySetInnerHTML={{ __html: description }}
				/>
			)}

			<OtpForm
				otpLength={inputLength}
				isLoading={isLoading}
				onResend={onResend}
				onChange={onChange}
				onSubmit={handleSubmit(onSubmit)}
				errorMessage={error ?? errors.otp?.message}
				resendOtpLoading={resendOtpLoading || resendOtpFetching}
				resendOtp={resendOtp}
				value={otpValue}
				buttonTitle={buttonTitle}
				cta={otpCta}
			/>
		</div>
	);
};

export default Otp;
